/**
 * Share of a question's answerers who picked an option, as a whole percent.
 *
 * Divides by the question's own answered count, never the poll's respondent
 * count: people skip questions, so dividing by everyone who took the poll would
 * understate every option. For a multi-select question the shares can add up
 * past 100.
 */
export function getOptionPercentage(count: number, answeredCount: number): number {
  if (answeredCount <= 0) {
    return 0;
  }

  return Math.round((count / answeredCount) * 100);
}

/** The value the API gives the single option that collects every answer outside a question's choices. */
export const UNLISTED_OPTION_VALUE = '__unlisted__';

function isSameValue(first: unknown, second: unknown): boolean {
  return String(first) === String(second);
}

function toPickedValues(answer: unknown): unknown[] {
  const values = Array.isArray(answer) ? answer : [answer];

  return values.filter((value) => value !== undefined && value !== null && value !== '');
}

/**
 * Whether a respondent's stored answer picked a result option.
 *
 * Values are compared as strings because a rating or choice can come back from
 * the API as a number where the stored answer holds a string, or the other way
 * round. A multi-select answer is a list. The unlisted option stands for every
 * answer outside the question's choices, so it is the respondent's when one of
 * their values matches none of the listed ones.
 */
export function isOptionAnswered(
  answer: unknown,
  optionValue: unknown,
  listedValues: unknown[]
): boolean {
  const picked = toPickedValues(answer);

  if (isSameValue(optionValue, UNLISTED_OPTION_VALUE)) {
    return picked.some((value) => !listedValues.some((listed) => isSameValue(value, listed)));
  }

  return picked.some((value) => isSameValue(value, optionValue));
}

/** Whether a respondent's matrix answer put a row in a column. A matrix answer maps each row to its column. */
export function isMatrixCellAnswered(
  answer: unknown,
  rowValue: unknown,
  columnValue: unknown
): boolean {
  if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
    return false;
  }

  const rowAnswer = (answer as Record<string, unknown>)[String(rowValue)];

  return toPickedValues(rowAnswer).some((value) => isSameValue(value, columnValue));
}

export function formatVoteCount(count: number): string {
  return `${count} ${count === 1 ? 'vote' : 'votes'}`;
}

export function formatAnsweredCount(count: number): string {
  return `${count} answered`;
}

export function formatRespondentCount(count: number): string {
  return `${count} ${count === 1 ? 'person' : 'people'} answered this poll`;
}
